import { forwardRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';

import { useMergedFieldAria } from './FormField';
import styles from './TextInput.module.css';

export type FieldSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: FieldSize;
  invalid?: boolean;
  /** For canonical ids (`act:S2.1`) — mono, so a typo is visible. */
  monospace?: boolean;
}

/**
 * A Carbon field: filled, bottom rule, no radius. The bottom rule is
 * `--c-border-control` (3.32:1), not `--c-border-subtle` — a control's only
 * boundary must be a control boundary (DESIGN-DECISION §7.5). No rule here
 * removes the outline: that was the mockups' §7.3 blocking defect.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { size = 'md', invalid = false, monospace = false, className, type = 'text', ...rest },
  ref,
) {
  const aria = useMergedFieldAria({
    id: rest.id,
    describedBy: rest['aria-describedby'],
    invalid,
    required: rest.required,
  });

  return (
    <input
      {...rest}
      {...aria}
      ref={ref}
      type={type}
      className={clsx(styles.input, styles[size], monospace && styles.mono, className)}
    />
  );
});
